<script setup lang="ts">
  import RecipientCard from '@/components/drawer/DrawerRecipients/RecipientCard.vue'
  import RoleAdder from './RoleAdder.vue'
  import { useAppStore } from '@/store/app'
  import { useBuilderStore } from '@/store/builder'
  import {
    AddCustomFieldModal,
    CustomValueLinkageItems,
    CustomValueLinkageOperationType,
    DEFAULT_DATE_FORMAT,
    DateFieldElementOptions,
    ELEMENTS_META,
    ICustomFieldsLinkageForm,
    IElement,
    IRecipient,
    RecipientEntityTypeEnum,
    TemplateTypeEnum,
    dateFieldDateValidationOptions,
    dateFormatOptions,
    generateCustomLinkItemPayload,
    getMarginProperties,
    getPaddingProperties,
    renderLabelAvatarOnSelect,
    useCustomFieldsForLinkage,
  } from '@gohighlevel/ghl-proposals-common'
  import { UIButton, UICheckbox, UIDivider, UIFormItem, UIInput, UISelect, UITextXsRegular, UIContentWrap } from '@gohighlevel/ghl-ui'
  import { debounce } from 'lodash'
  import { PropType, computed, onMounted, ref, toRaw, watch } from 'vue'
  import { useElementOption } from '../../../composable/elementOptions.js'
  import { ADD_ROLE_OPTION_VALUE, useRecipients } from '../../../composition/useRecipients.js'
  import { useRoleModal } from '../../../composition/useRoleModal'
  import { useI18n } from 'vue-i18n'
  const i18nLocale = useI18n()
  const { t } = i18nLocale

  const dateFieldDateValidationOptionsComputed = dateFieldDateValidationOptions()
  const props = defineProps({
    element: Object as PropType<IElement<DateFieldElementOptions>>,
  })

  const { updateOption } = useElementOption()
  const { syncRecipient, syncCustomFieldsForLinkage } = useBuilderStore()

  const builderStore = useBuilderStore()
  const { dateCustomFields } = useCustomFieldsForLinkage()
  const { signedByOptionsWithStaff, assignRecipientToFillableElement, getRoleAsRecipient } = useRecipients()
  const showLinkFieldModal = ref(false)
  const { showAddRoleModal, roleToEdit, openAddRole, openEditRole } = useRoleModal()
  const cvlOperationType = ref(CustomValueLinkageOperationType.ADD)
  const cvlData = ref()
  const dateCustomFieldOptions = computed(() => dateCustomFields.value)
  const recipients = computed(() => appStore.document?.recipients)
  const appStore = useAppStore()
  const recipient = ref()
  const selectValue = ref()
  const searchQuery = ref('')
  const margin = ref(getMarginProperties(props.element))
  const padding = ref(getPaddingProperties(props.element))
  const customValueLinkage = computed(() => builderStore.getCustomValueLinkage(props?.element?.id) || [])
  const docVariableList = computed(() => appStore.document?.variables || [])

  onMounted(() => {
    syncRecipientList()
  })

  const syncRecipientList = () => {
    syncRecipient()
    fetchRecipientData()
    getSelectValue()
    syncCustomFieldsForLinkage()
  }

  const getSelectValue = () => {
    const recipientsList = toRaw(recipients.value)
    const lastSelectedId = props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof DateFieldElementOptions]

    const userStillExistsInRecipients =
      recipientsList.find((el: any) => {
        return el.id === lastSelectedId
      }) || (appStore.document?.roles || []).some((role: any) => role.name === lastSelectedId)

    if (lastSelectedId && !userStillExistsInRecipients) {
      selectValue.value = null
      return
    }

    if (
      props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof DateFieldElementOptions] &&
      props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof DateFieldElementOptions]
    ) {
      selectValue.value = `${props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof DateFieldElementOptions]}_${
        props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof DateFieldElementOptions]
      }`
    }
  }

  const fetchRecipientData = (recipientId?: string, entityName?: string) => {
    const assignedRecipient = recipients.value.find((elem: IRecipient) => {
      if (recipientId && entityName) {
        return elem.id === recipientId && elem.entityName === entityName
      } else {
        return (
          elem.id === props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof DateFieldElementOptions] &&
          elem.entityName === props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof DateFieldElementOptions]
        )
      }
    })
    if (assignedRecipient) {
      recipient.value = assignedRecipient
    } else {
      const roleRecipient = getRoleAsRecipient(
        (recipientId ?? props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof DateFieldElementOptions]) as string | undefined
      )
      if (roleRecipient) recipient.value = roleRecipient
    }
  }

  const assignRecipientToTextField = (val: string) => {
    if (val === ADD_ROLE_OPTION_VALUE) {
      openAddRole()
      syncRecipientList()
      return
    }
    const recipientData = val.split('_')
    if (recipientData.length !== 2 || !props.element) {
      return
    }

    const recipientId = recipientData[0]
    const entityName = recipientData[1]

    // Use the shared function to assign recipient
    assignRecipientToFillableElement(val, props.element)

    // Fetch recipient data and sync
    fetchRecipientData(recipientId, entityName)
    syncRecipientList()
  }

  watch(props, newValue => {
    if (newValue) {
      syncRecipientList()
    }
    if (props.element) {
      margin.value = getMarginProperties(props.element)
      padding.value = getPaddingProperties(props.element)
    }
  })

  const isContactRecipient = computed(
    () => props.element?.component.options.recipient && props.element?.component.options.entityName === RecipientEntityTypeEnum.CONTACTS
  )

  const contactCustomValueMapping = computed(() => {
    return builderStore.getContactCustomValueLinkMapping()
  })

  const toggleModal = () => {
    showLinkFieldModal.value = !showLinkFieldModal.value
  }

  const availableDocumentVariablesList = computed(() => {
    const docDefaultVariable = docVariableList.value.map(vb => vb.fieldKey)
    builderStore.getAllLinkedDocumentVariables().forEach(vb => {
      if (!docDefaultVariable.includes(vb.fieldKey)) {
        docDefaultVariable.push(vb.fieldKey)
      }
    })
    return docDefaultVariable || []
  })

  const customVariableAdded = (formValue: ICustomFieldsLinkageForm) => {
    const operationType = formValue?.operationType
    const payload = generateCustomLinkItemPayload(formValue)
    if (operationType === CustomValueLinkageOperationType.ADD) {
      builderStore.updateCustomValueLinkage(formValue.elementId, formValue.elementType, payload)
    }
    if (operationType === CustomValueLinkageOperationType.EDIT) {
      builderStore.updateCustomValueLinkageItem(formValue.elementId, payload, formValue?.operationValue?.data)
    }
    debounce(() => (showLinkFieldModal.value = false), 400)()
  }

  const customVariableDeleted = (event: { type: string; data: any; element: any }) => {
    const elementId = event.element.id
    const filedKey = event.data.fieldKey
    builderStore.deleteCustomValueLinkage(elementId, filedKey)
  }

  const addMoreCustomFields = () => {
    cvlOperationType.value = CustomValueLinkageOperationType.ADD
    showLinkFieldModal.value = true
  }

  const customVariableEdit = (event: { type: string; data: any; element: any }) => {
    cvlData.value = event
    cvlOperationType.value = CustomValueLinkageOperationType.EDIT
    showLinkFieldModal.value = true
  }

  const isContentLibraryTemplate = computed(() => {
    return appStore.document.type === TemplateTypeEnum.CONTENT_LIBRARY
  })

  const filteredSignedByOptions = computed(() => {
    if (!searchQuery.value || searchQuery.value.trim() === '') {
      return signedByOptionsWithStaff.value
    }
    const query = searchQuery.value.toLowerCase().trim()
    return signedByOptionsWithStaff.value.filter((option: any) => {
      const label = option.label?.toLowerCase() || ''
      return label.includes(query)
    })
  })

  const handleSearch = (query: string) => {
    searchQuery.value = query
  }
</script>

<template>
  <AddCustomFieldModal
    :operation-type="cvlOperationType"
    :operation-value="cvlData"
    :element="element"
    :show="showLinkFieldModal"
    @on-cancel="toggleModal"
    :available-document-variables="availableDocumentVariablesList"
    @on-success="customVariableAdded"
    :menu-options="dateCustomFieldOptions"
    :available-custom-value-links="customValueLinkage"
    :contact-custom-value-mapping="contactCustomValueMapping"
    :is-document-variable-disabled="isContentLibraryTemplate"
  />
  <div class="hl-proposal-sidebar-content hl-proposal-textfield-properties">
    <div class="mb-4">
      <UIFormItem :label="$t('common.toBeFilledBy')">
        <UISelect
          :id="`signature-text`"
          :placeholder="$t('common.assignRecipientToTextField')"
          :options="filteredSignedByOptions"
          type="avatar"
          :value="selectValue"
          filterable
          @update:value="(val: any) => assignRecipientToTextField(val)"
          @search="handleSearch"
          :render-label="renderLabelAvatarOnSelect"
          :disabled="isContentLibraryTemplate"
        />
      </UIFormItem>
      <RoleAdder v-model:show="showAddRoleModal" :role="roleToEdit" @added="(val: string) => assignRecipientToTextField(val)" />
      <div>
        <RecipientCard :recipient="recipient" @edit-role="openEditRole" />
      </div>
    </div>
    <UITextXsRegular class="font-medium text-gray-400">
      {{ $t('common.options') }}
    </UITextXsRegular>
    <UIDivider />
    <UIContentWrap :locale="i18nLocale?.locale?.value || 'en-US'" class="p-0" style="display: contents">
      <UIFormItem :label="$t('common.placeholder')">
        <UIInput
          :id="`${element?.id}-placeholder`"
          :model-value="element?.component.options.placeholder"
          @update:model-value="val => updateOption(ELEMENTS_META.PLACEHOLDER, val)"
        />
      </UIFormItem>
    </UIContentWrap>

    <!-- <UIFormItem label="Field ID">
      <UIInput
        :id="`${element?.id}-field_id`"
        :model-value="element?.component.options.fieldId"
        @update:model-value="val => updateOption(ELEMENTS_META.FIELD_ID, val)"
      />
    </UIFormItem> -->
    <UIFormItem :label="$t('common.dateFormats')">
      <UISelect
        :default-value="DEFAULT_DATE_FORMAT"
        :options="dateFormatOptions"
        :id="`${element?.id}-date-formats`"
        :value="element?.component.options.dateFormat"
        @update:value="val => updateOption(ELEMENTS_META.DATE_FORMAT, val)"
      />
    </UIFormItem>
    <UIFormItem :label="$t('common.availableDates')">
      <UISelect
        :options="dateFieldDateValidationOptionsComputed"
        :id="`${element?.id}-available-dates`"
        :value="element?.component.options.availableDates"
        @update:value="val => updateOption(ELEMENTS_META.AVAILABLE_DATES, val)"
      />
    </UIFormItem>
    <UICheckbox
      :id="`${element?.id}-required-checkbox`"
      :disabled="false"
      :checked="element?.component.options.required"
      @update:checked="
        (val: boolean) => {
          updateOption(ELEMENTS_META.REQUIRED, val, element?.id)
        }
      "
    >
      {{ $t('common.required') }}
    </UICheckbox>

    <div v-if="isContactRecipient">
      <UITextXsRegular class="font-medium text-gray-400 mt-6 uppercase">
        {{ $t('common.linkedFields') }}
      </UITextXsRegular>
      <UIDivider />
      <CustomValueLinkageItems
        :custom-value-linkage="customValueLinkage"
        :element="element"
        @custom-value-linkage-delete="customVariableDeleted"
        @custom-value-linkage-edit="customVariableEdit"
      />
      <UIDivider v-if="customValueLinkage && customValueLinkage.length > 0" />
      <div class="mb-4">
        <UIButton class="w-full" id="add-more-linked-fields-cta-button" type="tertiary" @click="addMoreCustomFields">
          {{ customValueLinkage && customValueLinkage.length > 0 ? $t('common.addMoreCustomFields') : $t('common.addCustomFields') }}
        </UIButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .n-avatar__text {
    transform: translateX(-50%) translateY(-50%) scale(1);
  }
  .hl-proposal-textfield-properties {
    .n-divider.n-divider--no-title {
      margin: 10px 0px;
    }
  }
</style>
